/**
 * ServiceNotesModal
 * -----------------------------------------------------------------------------
 * Full Service-Notes editor as a popup. Everything the SM needs lives here:
 *
 *   ┌─ Service Notes (Modal) ─────────────────────────────────────────┐
 *   │  [Free-text rich notes — customer-facing]                       │
 *   │                                                                 │
 *   │  Quote Builder                                                  │
 *   │  + Add Row (inline: desc · qty · parts · margin · hrs · rate)   │
 *   │  ☐  Front brake pads & rotors   ×1    $412.00   [+JC] [✕]      │
 *   │  ☐  Roadworthy minor fix         ×1    $120.00   ✓ Added to JC │
 *   │  ...                                                            │
 *   │                                                                 │
 *   │  [Add Approved to Job Card]  [Add Approved to Invoice Lines*]   │
 *   │  *Enabled only after JC = completed                             │
 *   │                                                                 │
 *   │  [Cancel]                                       [Save & Close]  │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Per-row JC indicator: each row shows a green "✓ Added to JC" badge
 * once it has been pushed to the linked job card. The "Add Approved to
 * Invoice Lines" button is disabled until the linked Job Card's status
 * is 'completed' — this enforces the "complete the work before billing"
 * gate that the SM described.
 *
 * On successful "Add to Invoice Lines", the moved rows disappear from
 * notes_items (handled server-side); only the free-text comments remain.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Wrench,
  ArrowRightCircle,
  CheckCircle2,
  Lock,
  FileText,
  Sparkles,
  Undo2,
  ListPlus,
  CalendarPlus,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import ServiceNoteTemplatesPicker from '@/components/invoices/ServiceNoteTemplatesPicker';
import SupplierCombobox from './SupplierCombobox';
import CustomItemSearch from './CustomItemSearch';
import { computeRowPricing, orpToRrp as orpToRrpHelper } from '@/lib/pricing';
import { UNIT_OPTIONS } from '@/lib/units';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// iter225 — unified builder: the SAME EditorBlock used by the booking popup
// / quote editor / job-card quote section now renders the Service Notes
// add + edit forms, so all builders look and behave identically.
import { EditorBlock, getPricingDefaults } from '@/components/QuoteBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MARGIN_MIN_PCT = 40;
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
// Quote-row categories — mirrors the invoice line-item category ladder so
// SM has the same mental model on both screens.
//   • part_and_labour — most common (parts + workshop labour)
//   • part            — pure parts sale (no labour billed, e.g. battery sold off the shelf)
//   • labour          — pure labour (no parts billed, e.g. tyre swap, diagnostic, goodwill check)
//   • tyre            — special pricing: (ORP × (1 + manual margin%)) + flat-rate per tyre × qty — no RRP
const CATEGORY_OPTIONS = [
  { value: 'part_and_labour', label: 'Part & Labour' },
  { value: 'part',            label: 'Part only' },
  { value: 'labour',          label: 'Labour only' },
  { value: 'tyre',            label: 'Tyre (ORP + margin + flat)' },
];
const EMPTY_EDITOR = {
  category: 'part_and_labour',
  description: '',
  quantity: 1,
  unit: 'each',           // Mirrors InvoiceEdit unit selector — keeps qty
                          // semantics ("how many Each / Hours / Litres")
                          // identical between Quote Builder and Invoice.
  parts_cost_price: 0,   // ORP — what we paid supplier (informational)
  parts_supplier: '',    // Supplier name (free text + datalist)
  parts_price: 0,        // RRP — what we charge customer (drives formula)
  parts_margin_pct: 40,
  labour_hours: 0,
  labour_rate: 0,
  tyre_flat_rate: 0,     // Per-tyre flat fee (fitting + balance + valve + disposal)
};

export default function ServiceNotesModal({
  open,
  onOpenChange,
  invoice,
  invoiceId,
  workshopRate = 0,
  onChangeNotes,
  onChangeNotesItems,
  onInvoiceUpdated,
  onSaveInvoice,
  onBookCustomer,        // (selectedRows) => void — opens QuickBookingModal pre-filled
}) {
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ ...EMPTY_EDITOR, labour_rate: workshopRate });
  const [pricingDefaults, setPricingDefaults] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    category: 'part_and_labour',
    description: '', quantity: 1, unit: 'each', parts_supplier: '',
    parts_cost_price: 0, parts_price: 0, parts_margin_pct: 40,
    labour_hours: 0, labour_rate: 0, tyre_flat_rate: 0,
  });
  const [busy, setBusy] = useState(null);
  const [jcStatus, setJcStatus] = useState(null);
  // Map of {jc.line_items[].from_notes_item → completed bool} so we can gate
  // "Add Approved to Invoice Lines" PER notes row — only enable once the
  // mechanic has actually ticked off the corresponding JC task. Until then
  // the row is still "in progress" and shouldn't be invoiceable.
  const [jcLineCompletion, setJcLineCompletion] = useState({});

  // All notes rows on the invoice — both visible and soft-deleted (the
  // latter are preserved on the document so Get Findings can restore them
  // without losing structured pricing data).
  const allNotesRows = invoice.notes_items || [];
  // Visible rows in the modal — strip out soft-deleted ones.
  const rows = allNotesRows.filter(r => !r.removed_at);
  const deletedRows = allNotesRows.filter(r => r.removed_at);
  const deletedCount = deletedRows.length;

  // Bring back ALL soft-deleted notes rows with their full structured
  // pricing intact (supplier / ORP / RRP / margin / labour). Works for
  // rows of any origin (manually-built quote rows + Get-Findings imports).
  const restoreAllDeleted = () => {
    const now = new Date().toISOString();
    onChangeNotesItems(allNotesRows.map(r =>
      r.removed_at ? { ...r, removed_at: null, restored_at: now } : r,
    ));
    toast.success(`Restored ${deletedCount} deleted row${deletedCount === 1 ? '' : 's'} with full pricing intact`);
  };
  // Undo a single soft-deleted row (one-off restore).
  const restoreRow = (line_id) => {
    const now = new Date().toISOString();
    onChangeNotesItems(allNotesRows.map(r =>
      r.line_id === line_id ? { ...r, removed_at: null, restored_at: now } : r,
    ));
    toast.success('Row restored');
  };

  const invoiceLineIds = useMemo(
    () => new Set((invoice.line_items || []).map(li => li.line_id).filter(Boolean)),
    [invoice.line_items],
  );

  // Helper — is a notes row "currently on the invoice"? True only when it
  // was stamped AND the linked invoice line still exists (SM may have
  // deleted it by mistake — in that case the row is orphaned and ready
  // to push again).
  const isOnInvoiceLines = (r) =>
    !!r.added_to_invoice_lines_at && invoiceLineIds.has(r.invoice_line_id);

  // A notes row is "ready for invoice" only when it's been pushed to the JC
  // AND its corresponding JC line item is marked completed by the mechanic.
  // Rows that were never pushed to a JC fall back to the legacy gate
  // (whole JC completed).
  // A row is "ready" for invoice when:
  //   1. It's approved by the customer (`r.approved`)
  //   2. It's not already on the invoice (or its previous invoice line was deleted → re-eligible)
  //
  // The mechanic-completion gate is now SOFT (warning, not hard block) —
  // SM has ultimate authority and may invoice work in progress (deposits,
  // staged billing). The button stays enabled, the tooltip flags any rows
  // where the mechanic hasn't ticked the JC task done yet, and the backend
  // will still reject if the JC isn't in a billable state.
  const isRowReadyForInvoice = (r) => {
    if (!r.approved) return false;
    if (isOnInvoiceLines(r)) return false;  // already on invoice
    return true;
  };

  // For the soft warning tooltip — how many approved-not-yet-invoiced
  // rows are still waiting on the mechanic. Lets SM see at-a-glance
  // whether the work is finalized.
  const isRowMechIncomplete = (r) => {
    if (!r.approved || isOnInvoiceLines(r)) return false;
    if (!r.added_to_job_card_at) return jcStatus !== 'completed';
    return jcLineCompletion[r.line_id] !== true;
  };

  const approvedCount = rows.filter(r => r.approved).length;
  const approvedNotInJc = rows.filter(r => r.approved && !r.added_to_job_card_at).length;
  const approvedAlreadyOnJc = rows.filter(r => r.approved && r.added_to_job_card_at).length;
  // Only count approved items that aren't already on the invoice as
  // candidates for "Add to Invoice Lines". Rows whose linked invoice line
  // was deleted (orphan) are re-eligible.
  const approvedReadyForLines = rows.filter(isRowReadyForInvoice).length;
  const mechIncompleteCount = rows.filter(isRowMechIncomplete).length;
  const total = rows.reduce((s, r) => s + (Number(r.line_total) || 0), 0);

  // Rows imported from mechanic findings land with description + hours but
  // no ORP / RRP / supplier — the SM must enter those before customer
  // approval. Surface a hint count so it's obvious one tap (the ✏️ icon)
  // opens the inline editor for each.
  const rowsMissingPricing = rows.filter(r =>
    !r.removed_at && (
      (Number(r.parts_price) || 0) <= 0
      || (r.parts_supplier == null || String(r.parts_supplier).trim() === '')
    ),
  ).length;

  const hasLinkedJobCard = !!(invoice.job_card_id || invoice.booking_id);

  /* Load linked JC status + per-line completion when modal opens (so we
     can gate "Add to Invoice" both at JC-status level AND per-row level) */
  useEffect(() => {
    if (!open) return;
    const jcId = invoice.job_card_id;
    if (!jcId) { setJcStatus(null); setJcLineCompletion({}); return; }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/job-cards/${jcId}`, { credentials: 'include' });
        if (res.ok) {
          const jc = await res.json();
          setJcStatus(jc.status || null);
          // Treat ALL from-notes-items as mechanic-completed when EITHER:
          //   • The JC has progressed to sm_review / completed / delivered, OR
          //   • The mechanic has saved Step 5 "Work Performed" text
          //     (real-world: SMs and mechanics treat "Work Performed
          //     filled in" as the source of truth for whether the work
          //     happened — many never tick each individual Step 2 task).
          // Each row still honours explicit per-task ticks → if a task
          // is explicitly `completed: true` we keep that, but we no
          // longer require it to flip the row to "Completed".
          const status = jc.status || '';
          const jcAdvanced = status === 'sm_review' || status === 'completed' || status === 'delivered';
          const workPerformedFilled = !!(
            jc.work_performed &&
            String(jc.work_performed).trim().length > 0
          );
          const treatAllDone = jcAdvanced || workPerformedFilled;
          const map = {};
          for (const li of (jc.line_items || [])) {
            if (li.from_notes_item) {
              map[li.from_notes_item] = !!li.completed || treatAllDone;
            }
          }
          setJcLineCompletion(map);
        }
      } catch (_e) { /* noop */ }
    })();
  }, [open, invoice.job_card_id]);

  useEffect(() => {
    if (workshopRate && !newRow.labour_rate) {
      setNewRow(r => ({ ...r, labour_rate: workshopRate }));
    }
     
  }, [workshopRate]);

  /* Live total for the inline editor — uses shared computeRowPricing
   * helper. See /app/frontend/src/lib/pricing.js for the formula. */
  const newTotals = useMemo(() => {
    const p = computeRowPricing(newRow);
    const unit_price = p.parts_charge + (newRow.quantity > 0 ? p.labour_charge / (Number(newRow.quantity) || 1) : p.labour_charge);
    return { ...p, unit_price };
  }, [newRow]);

  const resetEditor = () => setNewRow({
    ...EMPTY_EDITOR,
    labour_rate: workshopRate || pricingDefaults?.labour_rate || EMPTY_EDITOR.labour_rate,
    parts_margin_pct: pricingDefaults?.parts_margin_pct ?? EMPTY_EDITOR.parts_margin_pct,
  });

  // iter225 — per-site pricing defaults (set on the Invoices page) seed
  // NEW rows only; existing rows keep their stored values.
  useEffect(() => {
    if (!open) return;
    getPricingDefaults().then(d => {
      setPricingDefaults(d);
      setNewRow(r => (adding ? r : {
        ...r,
        parts_margin_pct: d.parts_margin_pct,
        labour_rate: r.labour_rate || workshopRate || d.labour_rate,
      }));
    });
     
  }, [open]);

  // ORP → RRP auto-suggest helper — shared with InvoiceEdit via
  // /app/frontend/src/lib/pricing.js. Uses the workshop ORP margin %.
  const onChangeOrp = (setter, value) => {
    const next = { parts_cost_price: value };
    const orpNum = Number(value);
    if (isFinite(orpNum) && orpNum > 0) {
      next.parts_price = orpToRrpHelper(value, pricingDefaults?.orp_margin_pct);
    }
    setter(prev => ({ ...prev, ...next }));
  };

  const addRow = () => {
    const desc = (newRow.description || '').trim();
    if (!desc) { toast.error('Description is required'); return; }
    const cat = newRow.category || 'part_and_labour';
    const isTyre = cat === 'tyre';
    const wantsParts = cat === 'part' || cat === 'part_and_labour' || isTyre;
    const wantsLabour = cat === 'labour' || cat === 'part_and_labour';
    // iter225 — tyre margin is MANUAL (SM types it; blank = 0%).
    const margin = Number(newRow.parts_margin_pct) || 0;
    const parts = Number(newRow.parts_price) || 0;
    const cost = Number(newRow.parts_cost_price) || 0;
    const tyreFlat = Number(newRow.tyre_flat_rate) || 0;
    // RRP and labour can BOTH be zero (goodwill rows, free checks, in-house
    // courtesy). We only enforce margin/ORP rules when RRP > 0 on a row
    // that actually claims to bill for parts. Labour-only rows skip all
    // parts checks entirely. Tyre rows always require ORP (it's the
    // cost base of the formula).
    // Margin floor REMOVED (owner request) — the SM may enter ANY margin %.
    if (wantsParts && !isTyre && parts > 0 && cost <= 0) {
      toast.error('ORP (supplier cost) is required when RRP > 0');
      return;
    }
    if (isTyre && cost <= 0) {
      toast.error('Tyre rows need an ORP (supplier cost per tyre)');
      return;
    }
    const item = {
      line_id: `note_${Math.random().toString(36).slice(2, 10)}`,
      category: cat,
      description: desc,
      quantity: Number(newRow.quantity) || 1,
      // Unit label — Each / Hours / Litre / kg / Set / Pack — kept with
      // the row so quote→invoice promotion preserves it.
      unit: newRow.unit || (cat === 'labour' ? 'hours' : 'each'),
      line_total: Number(newTotals.line_total.toFixed(2)),
      // Parts block — zeroed out entirely on labour-only rows. No RRP
      // concept on tyre rows (parts_price 0).
      parts_price: isTyre ? 0 : (wantsParts ? parts : 0),
      parts_cost_price: wantsParts ? (cost || null) : null,
      parts_supplier: wantsParts ? ((newRow.parts_supplier || '').trim() || null) : null,
      // Store the margin explicitly (0 included) — null means "legacy row
      // from the locked-30% era" to the pricing engine.
      parts_margin_pct: wantsParts ? margin : null,
      // Labour block — zeroed out on part-only rows.
      // Tyre rows ALSO capture allocated hours (for mechanic productivity
      // attribution); they go straight to JC.quoted_labour_hours and
      // DON'T affect the price (price stays ORP×1.30 + flat). labour_rate
      // is 0 on tyre rows so pricing is unchanged.
      labour_hours: isTyre
        ? (Number(newRow.labour_hours) || 0)
        : (wantsLabour ? (Number(newRow.labour_hours) || 0) : 0),
      labour_rate: isTyre ? 0 : (wantsLabour ? (Number(newRow.labour_rate) || 0) : 0),
      // Tyre flat-rate — only stored when category=tyre.
      tyre_flat_rate: isTyre ? tyreFlat : null,
      approved: false,
      order: rows.length,
    };
    onChangeNotesItems([...allNotesRows, item]);
    resetEditor();
  };

  // Parse the customer-facing comments (HTML or plain text) into one
  // candidate quote row per non-empty line. Used by "Add lines to Quote
  // Builder" so the SM can paste a multi-line scope-of-work and have it
  // explode into pricing-ready rows in one click. Each line lands as a
  // part_and_labour row with quantity 1 and zero pricing — SM fills in
  // ORP / RRP / labour per row afterwards via the ✏️ on each row.
  const linesFromNotes = useMemo(() => {
    const html = invoice.notes || '';
    // Strip tags but keep line breaks: convert <br>/<p>/<li> closers to "\n"
    const text = html
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/<\/(p|div|li)>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#39;|&apos;/g, "'")
      .replace(/&quot;/g, '"');
    const out = [];
    const seen = new Set();
    for (const raw of text.split(/\r?\n/)) {
      let line = (raw || '').trim();
      if (!line) continue;
      // Strip common bullet markers
      for (const p of ['- ', '* ', '• ', '· ', '+ ']) {
        if (line.startsWith(p)) { line = line.slice(p.length).trim(); break; }
      }
      if (!line) continue;
      const key = line.toLowerCase().slice(0, 200);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(line.slice(0, 200));
    }
    // Filter out lines that already exist as a row (case-insensitive desc match)
    const existingDescs = new Set(
      rows.map(r => (r.description || '').trim().toLowerCase()),
    );
    return out.filter(l => !existingDescs.has(l.toLowerCase()));
  }, [invoice.notes, rows]);

  const addLinesAsQuoteRows = () => {
    if (linesFromNotes.length === 0) {
      toast.info('No new lines to add — write or paste line-separated content first.');
      return;
    }
    const now = Date.now();
    const newRows = linesFromNotes.map((desc, idx) => ({
      line_id: `ln_${now.toString(36)}_${idx}_${Math.random().toString(36).slice(2, 8)}`,
      category: 'part_and_labour',
      description: desc,
      quantity: 1,
      unit: 'each',
      parts_price: 0,
      parts_cost_price: 0,
      parts_supplier: null,
      parts_margin_pct: pricingDefaults?.parts_margin_pct ?? MARGIN_MIN_PCT,
      labour_hours: 0,
      labour_rate: workshopRate || 0,
      line_total: 0,
      approved: false,
      order: rows.length + idx,
      removed_at: null,
    }));
    onChangeNotesItems([...allNotesRows, ...newRows]);
    toast.success(
      `Added ${newRows.length} row${newRows.length === 1 ? '' : 's'} — tap ✏️ on each to set parts / labour pricing.`,
    );
  };

  const toggleApproved = (line_id) =>
    onChangeNotesItems(allNotesRows.map(r => r.line_id === line_id ? { ...r, approved: !r.approved } : r));
  const setAll = (state) =>
    onChangeNotesItems(allNotesRows.map(r => r.removed_at ? r : { ...r, approved: state }));

  // SM clicks "Get Findings" — pull every un-imported mechanic finding from the
  // linked job card into the invoice's notes_items. Server-side stamps the
  // findings as imported so a second click is idempotent.
  const importFindings = async () => {
    if (!invoiceId || !invoice.job_card_id) {
      toast.error('Link a job card to this invoice first');
      return;
    }
    setBusy('import-findings');
    try {
      // Persist any in-flight edits first (skipLineValidation: pull-findings
      // is upstream of the line-item validation; SM hasn't even seen the
      // findings yet so existing partial rows shouldn't block the pull).
      if (onSaveInvoice) {
        const ok = await onSaveInvoice({ skipLineValidation: true });
        if (ok === false) {
          toast.error('Could not save first — please resolve errors and retry');
          return;
        }
      }
      const res = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/import-jc-findings`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.added > 0) {
          toast.success(data.message);
          // Merge by line_id so soft-deleted rows that weren't touched
          // remain on the document, and restored rows (which carry the
          // same line_id) replace their previous entry with removed_at=null.
          if (Array.isArray(data.notes_items_added) && data.notes_items_added.length) {
            const byId = new Map(allNotesRows.map(r => [r.line_id, r]));
            for (const r of data.notes_items_added) {
              byId.set(r.line_id, r);
            }
            onChangeNotesItems(Array.from(byId.values()));
          }
          onInvoiceUpdated?.('import-findings', data);
        } else {
          toast.info(data.message || 'No new findings to import');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to import findings');
      }
    } finally { setBusy(null); }
  };

  // SM clicks "Get Prev Invoice" — pull the UNCOMPLETED / pending quoted
  // items from this car's most recent previous invoice as new quote rows.
  const importPrevInvoice = async () => {
    if (!invoiceId) {
      toast.error('Save the invoice first');
      return;
    }
    setBusy('import-prev-invoice');
    try {
      if (onSaveInvoice) {
        const ok = await onSaveInvoice({ skipLineValidation: true });
        if (ok === false) {
          toast.error('Could not save first — please resolve errors and retry');
          return;
        }
      }
      const res = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/import-prev-invoice-report`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.added > 0) {
          toast.success(data.message);
          if (Array.isArray(data.notes_items_added) && data.notes_items_added.length) {
            const byId = new Map(allNotesRows.map(r => [r.line_id, r]));
            for (const r of data.notes_items_added) {
              byId.set(r.line_id, r);
            }
            onChangeNotesItems(Array.from(byId.values()));
          }
          onInvoiceUpdated?.('import-prev-invoice', data);
        } else {
          toast.info(data.message || 'No uncompleted items on the previous invoice');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to pull previous invoice report');
      }
    } finally { setBusy(null); }
  };

  const removeRow = (line_id) => {
    // Soft-delete: stamp removed_at and KEEP the row on the document so Get
    // Findings can RESTORE it (preserving all structured ORP/RRP/margin/
    // labour data). Visible UI hides removed rows; restore brings them back.
    const allRows = invoice.notes_items || [];
    onChangeNotesItems(allRows.map(r =>
      r.line_id === line_id
        ? { ...r, removed_at: new Date().toISOString() }
        : r,
    ));
    // Toast with one-click Undo so an accidental delete is recoverable in
    // a single click without scrolling up to the banner.
    toast('Row deleted — structured pricing kept', {
      action: {
        label: 'Undo',
        onClick: () => restoreRow(line_id),
      },
      duration: 8000,
    });
  };
  const startEdit = (r) => {
    // Infer category from the saved row if not stored. Legacy rows
    // saved before the category field existed get classified by which
    // pricing fields are populated.
    let cat = r.category;
    if (!cat) {
      const hasTyreFlat = (Number(r.tyre_flat_rate) || 0) > 0;
      const hasParts = (Number(r.parts_price) || 0) > 0 || (r.parts_cost_price != null && Number(r.parts_cost_price) > 0);
      const hasLabour = (Number(r.labour_hours) || 0) > 0;
      cat = hasTyreFlat ? 'tyre'
        : hasParts && !hasLabour ? 'part'
        : !hasParts && hasLabour ? 'labour'
        : 'part_and_labour';
    }
    setEditingId(r.line_id);
    setEditDraft({
      category: cat,
      description: r.description || '',
      quantity: r.quantity ?? 1,
      // Carry the unit forward so the edit form pre-fills correctly.
      unit: r.unit || (cat === 'labour' ? 'hours' : 'each'),
      parts_supplier: r.parts_supplier || '',
      parts_cost_price: r.parts_cost_price ?? 0,
      parts_price: r.parts_price ?? 0,
      // Legacy tyre rows from the locked-30% era show 30 so totals don't
      // shift; otherwise seed with the per-site default margin.
      parts_margin_pct: r.parts_margin_pct ?? (cat === 'tyre' ? 30 : (pricingDefaults?.parts_margin_pct ?? 40)),
      labour_hours: r.labour_hours ?? 0,
      labour_rate: r.labour_rate ?? workshopRate ?? 0,
      tyre_flat_rate: r.tyre_flat_rate ?? 0,
    });
  };
  // Live total for the edit existing-row form — uses shared
  // computeRowPricing helper. See /app/frontend/src/lib/pricing.js.
  const editTotals = useMemo(() => computeRowPricing(editDraft), [editDraft]);
  const saveEdit = () => {
    // RRP, labour and ORP can all be 0 — SM has full latitude for special
    // pricing / goodwill rows. Margin is still recommended ≥ 40% when
    // RRP > 0 but is not blocking on the inline edit (the daily ORP
    // rollup reads whatever values were saved).
    const cat = editDraft.category || 'part_and_labour';
    const isTyre = cat === 'tyre';
    const wantsParts = cat === 'part' || cat === 'part_and_labour' || isTyre;
    const wantsLabour = cat === 'labour' || cat === 'part_and_labour';
    onChangeNotesItems(allNotesRows.map(r => r.line_id === editingId ? {
      ...r,
      category: cat,
      description: editDraft.description,
      quantity: Number(editDraft.quantity) || 1,
      // Persist unit alongside qty so future loads (and quote→invoice
      // promotion) render it consistently.
      unit: editDraft.unit || (cat === 'labour' ? 'hours' : 'each'),
      // Parts block — zeroed/null on labour-only rows so downstream
      // calculations (PDF, ORP rollup) don't see ghost values.
      parts_supplier: wantsParts ? ((editDraft.parts_supplier || '').trim() || null) : null,
      parts_cost_price: wantsParts ? (
        editDraft.parts_cost_price === '' || editDraft.parts_cost_price == null
          ? null
          : Number(editDraft.parts_cost_price)
      ) : null,
      parts_price: isTyre ? 0 : (wantsParts ? (Number(editDraft.parts_price) || 0) : 0),
      // iter225 — tyre margin is MANUAL (blank = 0%); stored explicitly.
      parts_margin_pct: wantsParts ? (Number(editDraft.parts_margin_pct) || 0) : null,
      // Labour block — zeroed on part-only rows.
      // Tyre rows capture allocated hours for mechanic productivity; the
      // value lands as JC.quoted_labour_hours and does NOT change pricing.
      labour_hours: isTyre
        ? (Number(editDraft.labour_hours) || 0)
        : (wantsLabour ? (Number(editDraft.labour_hours) || 0) : 0),
      labour_rate: isTyre ? 0 : (wantsLabour ? (Number(editDraft.labour_rate) || 0) : 0),
      // Tyre flat-rate — only persisted on tyre rows.
      tyre_flat_rate: isTyre ? (Number(editDraft.tyre_flat_rate) || 0) : null,
      line_total: Number(editTotals.line_total.toFixed(2)),
    } : r));
    setEditingId(null);
  };

  const callBulk = async (suffix, label) => {
    if (!invoiceId) { toast.error('Save the invoice first'); return; }
    const approvedIds = rows.filter(r => {
      if (!r.approved) return false;
      if (suffix === 'add-approved-to-invoice-lines') {
        // Per-row gate: mechanic must have ticked the JC task green.
        return isRowReadyForInvoice(r);
      }
      // add-approved-to-job-card path — skip already-on-JC rows.
      return !r.added_to_job_card_at;
    }).map(r => r.line_id);
    if (approvedIds.length === 0) {
      toast.error(suffix === 'add-approved-to-job-card'
        ? 'No new approved items to send to job card'
        : 'Tick the items to bill the customer for');
      return;
    }
    setBusy(suffix);
    try {
      // CRITICAL: persist the current modal state first so the backend can
      // find the rows by line_id. Without this, freshly-added rows live only
      // in React state and the API call fails with "no approved items".
      //
      // We pass `skipLineValidation: true` because this preflight is for
      // syncing modal state, not finalising the invoice. The full line-item
      // validation (supplier / margin / etc.) still gates the SM's explicit
      // Save / Send actions, but should not block a notes → invoice-lines
      // push because of unrelated partial rows the SM is still working on.
      if (onSaveInvoice) {
        const ok = await onSaveInvoice({ skipLineValidation: true });
        if (ok === false) {
          toast.error('Could not save the invoice — please fix any errors and try again');
          return;
        }
      }
      const res = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/${suffix}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line_ids: approvedIds }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `${label}: ${approvedIds.length} item${approvedIds.length === 1 ? '' : 's'}`);
        // Reload the full invoice from the response so notes_items + line_items are in sync
        onInvoiceUpdated?.(suffix, data);
      } else {
        const err = await res.json().catch(() => ({}));
        // iter138.9af — server may return a structured error
        // {code: 'JC_IN_PROGRESS', message, assigned_mechanic_name}
        // when the JC is still being worked on. Surface it with the
        // full message so the SM knows EXACTLY which mechanic to wait
        // on (and what to do next).
        let detail = err?.detail;
        if (typeof detail === 'object' && detail?.message) {
          toast.error(detail.message, { duration: 12000 });
        } else if (typeof detail === 'string') {
          toast.error(detail, { duration: 8000 });
        } else {
          toast.error(`Failed to ${label}`);
        }
      }
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[80vw] w-[80vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="service-notes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            Service Notes — {invoice.doc_no || 'New Invoice'}
          </DialogTitle>
          <DialogDescription>
            Type customer-facing notes and build the quote inline. Tick items the customer approves, then push them to the Job Card. When the work is done, push them to the Invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Free-text rich notes */}
          <div>
            <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
              <label className="text-sm font-semibold text-slate-700">Customer-facing comments</label>
              <ServiceNoteTemplatesPicker
                currentNotes={invoice.notes || ''}
                onInsert={(content) => onChangeNotes((invoice.notes ? invoice.notes + '<p></p>' : '') + (content || ''))}
              />
            </div>
            <RichTextEditor
              value={invoice.notes || ''}
              onChange={onChangeNotes}
              placeholder="Enter customer-facing comments (e.g. 'Thanks for choosing us. Your next service is due…')"
            />
            {/* Multi-line → Quote Builder shortcut. When the SM pastes a
                scope-of-work split across multiple lines, this button
                explodes each line into its own quote row (empty pricing
                — SM then taps the ✏️ on each row to set ORP / RRP /
                labour). De-duplicated against existing rows so re-clicks
                are safe. Hidden when there are no extractable lines. */}
            {linesFromNotes.length > 0 && (
              <div
                className="mt-2 flex items-center justify-between gap-3 px-2 py-1.5 rounded-md bg-emerald-50 border border-emerald-200"
                data-testid="snm-lines-to-rows-banner"
              >
                <p className="text-[11px] text-emerald-900">
                  <span className="font-semibold">{linesFromNotes.length} line{linesFromNotes.length === 1 ? '' : 's'}</span>
                  {' '}detected in comments above — convert to quote rows in one click. Pricing stays empty so you can fill it per row.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addLinesAsQuoteRows}
                  data-testid="snm-add-lines-as-rows"
                  className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-100 flex-shrink-0"
                >
                  <ListPlus className="h-3.5 w-3.5 mr-1" />
                  Add {linesFromNotes.length} line{linesFromNotes.length === 1 ? '' : 's'} to Quote Builder
                </Button>
              </div>
            )}
          </div>

          {/* Quote Builder */}
          <div className="border border-slate-200 rounded-lg bg-white" data-testid="snm-quote-section">
            {/* Deleted-rows recovery banner — appears whenever there is at
                least one soft-deleted notes row. Lets the SM bring back
                ALL deleted rows with full structured pricing (ORP / RRP /
                margin / labour / supplier) intact, regardless of whether
                the row originated from a finding or was hand-built. */}
            {deletedCount > 0 && (
              <div
                className="px-4 py-2.5 border-b bg-amber-50/70 border-amber-200 flex items-center justify-between gap-3 rounded-t-lg"
                data-testid="snm-deleted-banner"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Undo2 className="h-4 w-4 text-amber-700 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    <span className="font-semibold">{deletedCount} deleted row{deletedCount === 1 ? '' : 's'}</span> — full ORP / RRP / margin / labour preserved.
                    <span className="hidden sm:inline text-amber-700/80"> Click to bring back exactly as you left them.</span>
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={restoreAllDeleted}
                  className="h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0"
                  data-testid="snm-restore-all"
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Restore Deleted ({deletedCount})
                </Button>
              </div>
            )}
            <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-50 rounded-t-lg">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-wide font-semibold text-slate-700">Quote Builder</span>
                {rows.length > 0 && (
                  <span className="text-[10px] text-slate-500">
                    {approvedCount}/{rows.length} approved · {approvedAlreadyOnJc} on job card
                  </span>
                )}
                {rowsMissingPricing > 0 && (
                  <span
                    className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-medium"
                    data-testid="snm-missing-pricing-hint"
                    title="Tap the ✏️ icon on each row to enter ORP / RRP / margin / supplier"
                  >
                    <Pencil className="h-2.5 w-2.5" />
                    {rowsMissingPricing} row{rowsMissingPricing === 1 ? '' : 's'} need pricing — tap ✏️ to edit
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {rows.length > 0 && (
                  <>
                    <button type="button" onClick={() => setAll(true)} className="text-[10px] text-slate-500 hover:text-emerald-700" data-testid="snm-select-all">select all</button>
                    <span className="text-slate-300 text-[10px]">·</span>
                    <button type="button" onClick={() => setAll(false)} className="text-[10px] text-slate-500 hover:text-rose-700" data-testid="snm-clear-all">clear</button>
                    <span className="text-slate-300 text-[10px]">·</span>
                  </>
                )}
                {/* Custom-item template search — SM types "wiper" → matching
                    Custom Item Templates (with full pricing snapshot) appear
                    in a dropdown. Pick one → all line_items splice into the
                    quote as fully-pre-filled, editable rows. */}
                {!adding && (
                  <CustomItemSearch
                    workshopRate={workshopRate}
                    onPickRows={(newRows) => {
                      const baseOrder = rows.length;
                      const stamped = newRows.map((r, i) => ({ ...r, order: baseOrder + i }));
                      onChangeNotesItems([...allNotesRows, ...stamped]);
                    }}
                  />
                )}
                {!adding && rows.length > 0 && (
                  <span className="text-slate-300 text-[10px]">·</span>
                )}
                {!adding && (
                  <>
                    {/* Pull the mechanic's structured findings straight into
                        the quote as new rows — SM then fills ORP/RRP. */}
                    {!!invoice.job_card_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={importFindings}
                        disabled={busy === 'import-findings'}
                        data-testid="snm-import-findings"
                        className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        title="Import any new findings the mechanic added on the linked job card as quote rows"
                      >
                        <Sparkles className="h-3 w-3 mr-1" />
                        {busy === 'import-findings' ? 'Pulling…' : 'Get Findings'}
                      </Button>
                    )}
                    {/* Pull uncompleted quoted items from this car's most
                        recent previous invoice as fresh quote rows. */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={importPrevInvoice}
                      disabled={busy === 'import-prev-invoice'}
                      data-testid="snm-import-prev-invoice"
                      className="h-7 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                      title="Pull uncompleted / not-actioned quoted items from this car's previous invoice"
                    >
                      <History className="h-3 w-3 mr-1" />
                      {busy === 'import-prev-invoice' ? 'Pulling…' : 'Get Prev Invoice'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="snm-add-row" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                      <Plus className="h-3 w-3 mr-1" /> Add Row
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Inline editor */}
            {adding && (
              <div className="px-4 py-3 border-b border-emerald-100 bg-emerald-50/20">
                <EditorBlock
                  draft={newRow}
                  setDraft={setNewRow}
                  totals={newTotals}
                  onCommit={addRow}
                  onCancel={() => { setAdding(false); resetEditor(); }}
                  showUnit
                  testIdPrefix="snm-editor"
                />
              </div>
            )}

            {/* Rows */}
            {rows.length === 0 && !adding ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="snm-empty">
                No quote rows yet. Click <span className="font-semibold text-slate-600">Add Row</span> to start building.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {rows.map(r => {
                  const isEditing = editingId === r.line_id;
                  const onJc = !!r.added_to_job_card_at;
                  const onInvoice = isOnInvoiceLines(r);
                  const wasOnInvoice = !!r.added_to_invoice_lines_at && !onInvoice; // orphan
                  // The mechanic's tick state on the linked JC line — drives
                  // the green "Completed" badge AND unlocks the
                  // "Add Approved to Invoice Lines" button per row.
                  const mechCompleted = jcLineCompletion[r.line_id] === true;

                  // iter138.5 — Mechanic-notes row. Imported from the JC's
                  // free-text "Additional notes" field via Get Findings.
                  // Renders as a YELLOW note (not a billable row) so the
                  // SM sees the mechanic's free-form remarks alongside
                  // the structured findings without them inflating the
                  // quote total. Excluded from approve / push-to-JC /
                  // push-to-invoice flows — it's purely informational.
                  if (r.note_type === 'mechanic_notes') {
                    return (
                      <li
                        key={r.line_id}
                        className="px-4 py-2.5 flex items-start gap-3 bg-amber-50 border-l-4 border-amber-400"
                        data-testid={`snm-mechanic-note-${r.line_id}`}
                      >
                        <div className="mt-0.5 text-amber-700 flex-shrink-0">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-200 text-amber-900 text-[10px] font-extrabold uppercase tracking-wide" title="Mechanic's additional free-text notes from the job card">
                            !
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-wide font-bold text-amber-800 mb-0.5">
                            Mechanic&apos;s additional notes · informational only
                          </p>
                          <p className="text-sm text-amber-900 whitespace-pre-wrap break-words">
                            {r.description}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeRow(r.line_id)}
                          title="Dismiss this note"
                          className="h-7 text-xs text-amber-700 hover:bg-amber-100"
                          data-testid={`snm-mechanic-note-dismiss-${r.line_id}`}
                        >
                          Dismiss
                        </Button>
                      </li>
                    );
                  }

                  return (
                    <li key={r.line_id} className={`px-4 py-2.5 flex items-start gap-3 ${onInvoice ? 'bg-emerald-50/60' : r.approved ? 'bg-emerald-50/30' : ''}`} data-testid={`snm-row-${r.line_id}`}>
                      <Checkbox
                        checked={!!r.approved}
                        onCheckedChange={() => toggleApproved(r.line_id)}
                        className="mt-1"
                        data-testid={`snm-approved-${r.line_id}`}
                        aria-label="Approved by customer"
                      />
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div data-testid={`snm-edit-form-${r.line_id}`}>
                            <EditorBlock
                              draft={editDraft}
                              setDraft={setEditDraft}
                              totals={editTotals}
                              onCommit={saveEdit}
                              onCancel={() => setEditingId(null)}
                              showUnit
                              isEdit
                              testIdPrefix={`snm-edit-${r.line_id}`}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold uppercase tracking-wide text-slate-900 truncate">{r.description}</p>
                              {onJc && (
                                <p className="text-[10px] text-emerald-700 flex items-center gap-1 mt-0.5 flex-wrap" data-testid={`snm-row-onjc-${r.line_id}`}>
                                  <CheckCircle2 className="h-3 w-3" /> Added to Job Card
                                  {mechCompleted ? (
                                    <span
                                      className="ml-1 bg-emerald-600 text-white px-1.5 py-0 rounded-full font-semibold inline-flex items-center gap-0.5"
                                      data-testid={`snm-row-mech-completed-${r.line_id}`}
                                    >
                                      <Check className="h-2.5 w-2.5" /> Completed
                                    </span>
                                  ) : (
                                    <span
                                      className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0 rounded-full font-medium inline-flex items-center gap-0.5"
                                      data-testid={`snm-row-mech-inprogress-${r.line_id}`}
                                    >
                                      <Wrench className="h-2.5 w-2.5" /> Mechanic working
                                    </span>
                                  )}
                                </p>
                              )}
                              {onInvoice && (
                                <p className="text-[10px] text-emerald-700 font-semibold flex items-center gap-1 mt-0.5" data-testid={`snm-row-oninvoice-${r.line_id}`}>
                                  <CheckCircle2 className="h-3 w-3" /> Added to Invoice Lines · keep visible for re-quote / restore
                                </p>
                              )}
                              {wasOnInvoice && (
                                <p className="text-[10px] text-amber-700 font-semibold flex items-center gap-1 mt-0.5" data-testid={`snm-row-orphan-${r.line_id}`}>
                                  <CheckCircle2 className="h-3 w-3" /> Invoice line was deleted — ready to re-add
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 tabular-nums w-12 text-right">× {r.quantity ?? 1}</p>
                            <p className="text-sm font-semibold text-slate-900 tabular-nums w-20 text-right">{fmt(r.line_total)}</p>
                            <div className="flex items-center gap-1">
                              {onInvoice ? (
                                <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] px-1.5 py-0" data-testid={`snm-badge-oninvoice-${r.line_id}`}>on Invoice</Badge>
                              ) : wasOnInvoice ? (
                                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] px-1.5 py-0" data-testid={`snm-badge-orphan-${r.line_id}`}>orphan</Badge>
                              ) : onJc ? (
                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0">on JC</Badge>
                              ) : null}
                              <button onClick={() => startEdit(r)} className="text-slate-400 hover:text-slate-700 p-1" data-testid={`snm-edit-${r.line_id}`} aria-label="Edit">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => removeRow(r.line_id)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`snm-remove-${r.line_id}`} aria-label="Remove">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {rows.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t bg-slate-50 rounded-b-lg">
                <p className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">Quote total:</span>{' '}
                  <span className="tabular-nums">{fmt(total)}</span>
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* iter138.9ao — "Book Customer" button.
                      Lets SM take the ticked (Approved) rows straight into a
                      new booking pre-filled with this invoice's customer +
                      vehicle. Selected rows are forwarded as pending-work
                      items so the booking shows them as the tasks to do. */}
                  {onBookCustomer && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy !== null || approvedCount === 0 || !invoice.customer_id}
                      onClick={() => {
                        const sel = rows
                          .filter(r => r.approved)
                          .map(r => ({
                            description: r.description,
                            category: r.category || 'part_and_labour',
                            quantity: r.quantity ?? 1,
                            unit: r.unit || 'each',
                            parts_price: r.parts_price ?? 0,
                            parts_cost_price: r.parts_cost_price ?? 0,
                            parts_supplier: r.parts_supplier || '',
                            labour_hours: r.labour_hours ?? 0,
                            labour_rate: r.labour_rate ?? 0,
                            line_total: r.line_total ?? 0,
                            source: 'quote',
                            source_invoice_id: invoice.invoice_id,
                            source_line_id: r.line_id,
                          }));
                        if (sel.length === 0) {
                          toast.error('Tick at least one row Approved to book it');
                          return;
                        }
                        onBookCustomer(sel);
                      }}
                      className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="snm-book-customer"
                      title={
                        !invoice.customer_id
                          ? 'Set a customer on this invoice first'
                          : approvedCount === 0
                            ? 'Tick at least one row Approved to book it'
                            : `Open booking modal with ${approvedCount} ticked item${approvedCount === 1 ? '' : 's'} as the booked tasks`
                      }
                    >
                      <CalendarPlus className="h-3.5 w-3.5 mr-1" />
                      {`Book Customer${approvedCount ? ` (${approvedCount})` : ''}`}
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline"
                    disabled={busy !== null || approvedNotInJc === 0 || !hasLinkedJobCard}
                    onClick={() => callBulk('add-approved-to-job-card', 'add to job card')}
                    data-testid="snm-add-to-jobcard"
                    title={!hasLinkedJobCard ? 'No job card linked' : approvedNotInJc === 0 ? 'Nothing new to push' : 'Push approved items into the linked Job Card'}
                  >
                    <Wrench className="h-3.5 w-3.5 mr-1" />
                    {busy === 'add-approved-to-job-card' ? 'Sending…' : `Add Approved to Job Card${approvedNotInJc ? ` (${approvedNotInJc})` : ''}`}
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy !== null || approvedReadyForLines === 0}
                    onClick={() => callBulk('add-approved-to-invoice-lines', 'add to invoice lines')}
                    className="bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-300"
                    data-testid="snm-add-to-lines"
                    title={
                      approvedReadyForLines === 0
                        ? 'Tick at least one row Approved to enable.'
                        : (mechIncompleteCount > 0
                            ? `Adds ${approvedReadyForLines} approved item(s) to the invoice. Heads up — ${mechIncompleteCount} of them aren't ticked complete on the JC yet (you can still invoice; staged billing is allowed).`
                            : `Adds ${approvedReadyForLines} mechanic-completed approved item(s) to the customer invoice.`)
                    }
                  >
                    <ArrowRightCircle className="h-3.5 w-3.5 mr-1" />
                    {busy === 'add-approved-to-invoice-lines'
                      ? 'Adding…'
                      : `Add Approved to Invoice Lines${approvedReadyForLines ? ` (${approvedReadyForLines})` : ''}`}
                  </Button>
                </div>
              </div>
            )}
            {rows.length > 0 && mechIncompleteCount > 0 && (
              <p className="px-4 pb-3 text-[11px] text-amber-700 flex items-center gap-1" data-testid="snm-jc-gate-hint">
                <Lock className="h-3 w-3" />
                {hasLinkedJobCard
                  ? `${mechIncompleteCount} approved row(s) still pending on the JC (status: ${jcStatus || 'unknown'}). You can still invoice — staged billing is OK.`
                  : 'Link a job card to track mechanic progress per row.'}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="snm-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
